/**
 * MeasureTab - 계측 탭 (통합 컴포넌트)
 * Feature: #393, #408, #432
 *
 * useMeasures hook + MeasureTypeSelector + MeasureItemCard + MeasurePhoto 통합
 * - 스와이프 탭 바로 타입별 필터링
 * - 서브탭 간 스와이프 이동 (좌우 translateX 애니메이션)
 * - 추가: 타입 선택 → 즉시 빈 카드 추가 (인라인 편집)
 * - 수정: 카드 필드 클릭 → FieldEditModal → 저장
 * - 삭제: 확인 → 삭제
 */

import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import type { TouchEvent as ReactTouchEvent } from "react";
import { Plus, Ruler } from "lucide-react";
import { MeasureType, MeasureTypeNames, createLogger, type MeasureTypeKeyType } from "@ict/shared";
import { measureTypeIcons } from "./measure/measureTypeIcons";
import { useMeasures } from "@/hooks/useMeasures";
import { useToast } from "@/contexts/ToastContext";
import { MeasureTypeSelector } from "./measure/MeasureTypeSelector";
import { MeasureItemCard } from "./measure/MeasureItemCard";

const logger = createLogger({ module: "MeasureTab" });

interface MeasureTabProps {
  projectId: string;
  docKey: string;
  machineId: string;
  isLoading?: boolean;
  /** 서브탭 상태 변경 알림 (첫번째 서브탭 여부) */
  onSubTabInfo?: (info: { isFirstSubTab: boolean }) => void;
  /** 외부에서 첫번째 서브탭으로 리셋할 수 있도록 ref 전달 */
  resetSubTabRef?: React.RefObject<(() => void) | null>;
}

export function MeasureTab({
  projectId,
  docKey,
  machineId,
  isLoading,
  onSubTabInfo,
  resetSubTabRef,
}: MeasureTabProps) {
  const {
    measures,
    loading,
    addMeasure,
    updateMeasure,
    updateMeasureLocalOnly,
    deleteMeasure,
    mutationLoading,
  } = useMeasures({ projectId, docKey, machineId });

  const [typeSelectorOpen, setTypeSelectorOpen] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: MeasureType; index: number } | null>(
    null
  );
  const [activeType, setActiveType] = useState<MeasureType | null>(null);
  // 인라인 추가 후 스크롤 위치 지정을 위한 ref
  const cardListRef = useRef<HTMLDivElement>(null);
  // 인라인 추가 후 스크롤 요청 플래그
  const scrollPendingRef = useRef(false);
  const { showToast } = useToast();

  // 서브탭 스와이프 관련 ref (크로스-핸들러 상태)
  const touchStartXRef = useRef(0);
  const touchStartYRef = useRef(0);
  const touchDirectionRef = useRef<"horizontal" | "vertical" | null>(null);
  const isSwipingRef = useRef(false);
  // 탭 바 자동 스크롤을 위한 ref
  const tabBarRef = useRef<HTMLDivElement>(null);
  const tabButtonRefs = useRef<Map<string, HTMLButtonElement>>(new Map());

  // 데이터가 있는 계측 타입 목록
  const registeredTypes = useMemo(() => {
    if (!measures) return [];
    return Object.entries(measures)
      .filter(([key, value]) => key !== "__typename" && Array.isArray(value) && value.length > 0)
      .map(([key]) => key as MeasureType);
  }, [measures]);

  // registeredTypes가 변경되면 activeType 업데이트
  useEffect(() => {
    if (registeredTypes.length === 0) {
      setActiveType(null);
    } else if (!activeType || !registeredTypes.includes(activeType)) {
      setActiveType(registeredTypes[0]);
    }
    // activeType을 deps에 포함하면 탭 전환 시 자기 자신을 리셋하므로 의도적 제외
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [registeredTypes]);

  // 외부에서 서브탭 리셋 함수 접근 가능하도록 ref에 등록
  useEffect(() => {
    if (resetSubTabRef) {
      resetSubTabRef.current = () => {
        if (registeredTypes.length > 0) {
          setActiveType(registeredTypes[0]);
        }
      };
    }
    return () => {
      if (resetSubTabRef) {
        resetSubTabRef.current = null;
      }
    };
  }, [registeredTypes, resetSubTabRef]);

  // 서브탭 상태 변경을 InspectionPage에 알림
  useEffect(() => {
    if (!onSubTabInfo) return;
    const isFirst =
      registeredTypes.length === 0 || !activeType || registeredTypes.indexOf(activeType) === 0;
    onSubTabInfo({ isFirstSubTab: isFirst });
  }, [activeType, registeredTypes, onSubTabInfo]);

  // activeType 변경 시 탭 바 자동 스크롤 (활성 탭이 보이도록)
  useEffect(() => {
    if (!activeType) return;
    const button = tabButtonRefs.current.get(activeType);
    const container = tabBarRef.current;
    if (!button || !container) return;
    // 전체 콘텐츠가 컨테이너보다 작으면 스크롤 불필요
    if (container.scrollWidth <= container.offsetWidth) return;
    const buttonCenter = button.offsetLeft + button.offsetWidth / 2;
    const targetScroll = buttonCenter - container.offsetWidth / 2;
    container.scrollTo({ left: Math.max(0, targetScroll), behavior: "smooth" });
  }, [activeType]);

  // 현재 활성 탭의 항목들
  const activeItems = useMemo(() => {
    if (!activeType || !measures) return [];
    const items = measures[activeType as keyof typeof measures];
    if (!Array.isArray(items)) return [];
    return items as Record<string, unknown>[];
  }, [activeType, measures]);

  // activeItems가 변경되고 스크롤 요청 플래그가 설정되었을 때 마지막 항목으로 스크롤
  useEffect(() => {
    if (scrollPendingRef.current && activeItems.length > 0) {
      scrollPendingRef.current = false;
      // DOM 렌더링 완료 후 스크롤 실행
      requestAnimationFrame(() => {
        cardListRef.current?.lastElementChild?.scrollIntoView({
          behavior: "smooth",
          block: "center",
        });
      });
    }
  }, [activeItems]);

  // 인라인 추가: 빈 카드를 즉시 추가하고 해당 탭으로 전환
  const handleInlineAdd = useCallback(
    async (type: MeasureType) => {
      const result = await addMeasure(type, {});
      if (result.success) {
        logger.info("계측 항목 인라인 추가 완료", { type });
        setActiveType(type);
        // 스크롤 플래그 설정 (activeItems useEffect에서 DOM 업데이트 후 스크롤)
        scrollPendingRef.current = true;
        showToast("계측 항목이 추가되었습니다", "success");
      } else {
        logger.error("계측 항목 추가 실패", { error: result.error });
      }
    },
    [addMeasure, showToast]
  );

  // 타입 선택 모달에서 타입 선택 → 즉시 추가
  const handleTypeSelect = useCallback(
    (type: MeasureType) => {
      setTypeSelectorOpen(false);
      handleInlineAdd(type);
    },
    [handleInlineAdd]
  );

  // 같은 타입 추가
  const handleSameTypeAdd = useCallback(
    (type: MeasureType) => {
      handleInlineAdd(type);
    },
    [handleInlineAdd]
  );

  // 서브탭 스와이프: 터치 시작
  const handleSwipeTouchStart = useCallback((e: ReactTouchEvent) => {
    touchStartXRef.current = e.touches[0].clientX;
    touchStartYRef.current = e.touches[0].clientY;
    touchDirectionRef.current = null;
  }, []);

  // 서브탭 스와이프: 터치 이동 (방향 결정)
  const handleSwipeTouchMove = useCallback((e: ReactTouchEvent) => {
    if (touchDirectionRef.current !== null) return;
    const deltaX = Math.abs(e.touches[0].clientX - touchStartXRef.current);
    const deltaY = Math.abs(e.touches[0].clientY - touchStartYRef.current);
    const threshold = 10;
    if (deltaX < threshold && deltaY < threshold) return;
    touchDirectionRef.current = deltaX > deltaY * 1.5 ? "horizontal" : "vertical";
  }, []);

  // 서브탭 스와이프: 터치 종료 (탭 전환)
  const handleSwipeTouchEnd = useCallback(
    (e: ReactTouchEvent) => {
      if (isSwipingRef.current) return;
      if (touchDirectionRef.current !== "horizontal") return;

      const diff = touchStartXRef.current - e.changedTouches[0].clientX;
      if (Math.abs(diff) < 50) return;

      if (registeredTypes.length <= 1 || !activeType) return;

      const currentIdx = registeredTypes.indexOf(activeType);
      if (currentIdx === -1) return;

      const direction = diff > 0 ? 1 : -1;
      const newIdx = currentIdx + direction;

      // 경계 처리: 첫 번째/마지막 서브탭에서 더 이상 이동 안 함
      if (newIdx < 0 || newIdx >= registeredTypes.length) return;

      isSwipingRef.current = true;
      setActiveType(registeredTypes[newIdx]);
      setTimeout(() => {
        isSwipingRef.current = false;
      }, 300);
    },
    [registeredTypes, activeType]
  );

  // 뒤로가기 이벤트로 deleteConfirm 다이얼로그/typeSelectorOpen 닫기
  useEffect(() => {
    if (!deleteConfirm && !typeSelectorOpen) return;
    const handleCloseTopModal = (e: Event) => {
      e.preventDefault();
      if (deleteConfirm) {
        setDeleteConfirm(null);
      } else if (typeSelectorOpen) {
        setTypeSelectorOpen(false);
      }
    };
    document.addEventListener("closeTopModal", handleCloseTopModal);
    return () => document.removeEventListener("closeTopModal", handleCloseTopModal);
  }, [deleteConfirm, typeSelectorOpen]);

  // 삭제 확인
  const handleDeleteRequest = useCallback((type: MeasureType, index: number) => {
    setDeleteConfirm({ type, index });
  }, []);

  // 삭제 실행
  const handleDeleteConfirm = useCallback(async () => {
    if (!deleteConfirm) return;
    const result = await deleteMeasure(deleteConfirm.type, deleteConfirm.index);
    if (result.success) {
      logger.info("계측 항목 삭제 완료", { type: deleteConfirm.type });
    } else {
      logger.error("계측 항목 삭제 실패", { error: result.error });
    }
    setDeleteConfirm(null);
  }, [deleteConfirm, deleteMeasure]);

  // 로딩 상태
  if (isLoading || loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-gray-300 border-t-[#3182F6]" />
      </div>
    );
  }

  // 목록 모드
  return (
    <div className="flex flex-col bg-[#F7F8FA] h-full">
      {/* 빈 상태: 스와이프 탭 바 숨김, 중앙 CTA */}
      {registeredTypes.length === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center px-6 py-20">
          <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-blue-50">
            <Ruler className="h-8 w-8 text-blue-400" />
          </div>
          <p className="mt-4 text-[15px] font-medium text-gray-700">등록된 계측 항목이 없습니다</p>
          <p className="mt-1.5 text-[13px] text-gray-400">계측 항목을 등록해보세요</p>
          <button
            type="button"
            onClick={() => setTypeSelectorOpen(true)}
            className="mt-6 flex items-center gap-1.5 rounded-xl bg-[#3182F6] px-5 py-3 text-[14px] font-semibold text-white active:bg-[#2272E6] transition-colors"
          >
            <Plus className="h-4 w-4" />
            계측 추가
          </button>
        </div>
      ) : (
        <>
          {/* 스와이프 탭 바 */}
          <div className="flex items-center bg-white border-b border-gray-100">
            {/* 스크롤 탭 영역 */}
            <div ref={tabBarRef} className="flex-1 overflow-x-auto hide-scrollbar">
              <div className="flex gap-0 px-2" role="tablist" style={{ minWidth: "min-content" }}>
                {registeredTypes.map((type) => {
                  const typeName = MeasureTypeNames[type as keyof typeof MeasureTypeNames] ?? type;
                  const TabIcon = measureTypeIcons[type as MeasureTypeKeyType] ?? Ruler;
                  const isActive = type === activeType;
                  return (
                    <button
                      key={type}
                      ref={(el) => {
                        if (el) tabButtonRefs.current.set(type, el);
                        else tabButtonRefs.current.delete(type);
                      }}
                      type="button"
                      role="tab"
                      aria-selected={isActive}
                      onClick={() => setActiveType(type)}
                      className={`flex-shrink-0 flex items-center gap-1 px-3 py-3 text-[13px] font-medium whitespace-nowrap border-b-2 transition-colors ${
                        isActive
                          ? "border-[#3182F6] text-[#3182F6] font-semibold"
                          : "border-transparent text-gray-400"
                      }`}
                    >
                      <TabIcon className="h-3.5 w-3.5" />
                      {typeName}
                    </button>
                  );
                })}
              </div>
            </div>
            {/* 고정 + 버튼 */}
            <button
              type="button"
              onClick={() => setTypeSelectorOpen(true)}
              aria-label="계측 타입 추가"
              className="flex-shrink-0 flex h-full items-center px-3 border-l border-gray-100"
            >
              <Plus className="h-5 w-5 text-[#3182F6]" />
            </button>
          </div>

          {/* 서브탭 콘텐츠 (스와이프 + translateX 애니메이션) */}
          <div
            className="relative flex-1 overflow-hidden touch-pan-y"
            onTouchStart={handleSwipeTouchStart}
            onTouchMove={handleSwipeTouchMove}
            onTouchEnd={handleSwipeTouchEnd}
          >
            {registeredTypes.map((type, idx) => {
              const rawIdx = activeType ? registeredTypes.indexOf(activeType) : 0;
              const activeIdx = rawIdx < 0 ? 0 : rawIdx;
              const offset = (idx - activeIdx) * 100;
              const items = measures?.[type as keyof typeof measures];
              const typeItems = Array.isArray(items) ? (items as Record<string, unknown>[]) : [];

              return (
                <div
                  key={type}
                  className="absolute inset-0 overflow-y-auto transition-transform duration-300 ease-out touch-pan-y"
                  style={{ transform: `translateX(${offset}%)` }}
                >
                  <div className="flex items-center justify-between px-4 pt-4 pb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-[15px] font-semibold text-gray-800">
                        {MeasureTypeNames[type as keyof typeof MeasureTypeNames]}
                      </span>
                      <span className="rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700">
                        {typeItems.length}개
                      </span>
                    </div>
                    <button
                      type="button"
                      onClick={() => handleSameTypeAdd(type)}
                      className="flex h-8 items-center gap-1 rounded-lg bg-[#3182F6] px-2.5 text-[12px] font-medium text-white active:bg-[#2272E6] transition-colors"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      추가
                    </button>
                  </div>
                  <div
                    ref={type === activeType ? cardListRef : undefined}
                    className="flex flex-col gap-3 px-4 py-4"
                  >
                    {typeItems.map((item, itemIdx) => (
                      <MeasureItemCard
                        key={`${type}-${itemIdx}`}
                        measureType={type}
                        data={item}
                        index={itemIdx}
                        displayNumber={itemIdx + 1}
                        onDelete={handleDeleteRequest}
                        mutationLoading={mutationLoading}
                        projectId={projectId}
                        docKey={docKey}
                        machineId={machineId}
                        updateMeasure={updateMeasure}
                        updateMeasureLocalOnly={updateMeasureLocalOnly}
                      />
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* 타입 선택 모달 */}
      <MeasureTypeSelector
        open={typeSelectorOpen}
        onClose={() => setTypeSelectorOpen(false)}
        onSelect={handleTypeSelect}
        registeredTypes={registeredTypes}
      />

      {/* 삭제 확인 다이얼로그 */}
      {deleteConfirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="mx-6 w-full max-w-sm rounded-2xl bg-white p-6 shadow-xl">
            <h3 className="text-[16px] font-semibold text-gray-900">계측 항목 삭제</h3>
            <p className="mt-2 text-[14px] text-gray-500">
              {MeasureTypeNames[deleteConfirm.type as keyof typeof MeasureTypeNames] ??
                deleteConfirm.type}{" "}
              항목을 삭제하시겠습니까?
            </p>
            <div className="mt-5 flex gap-3">
              <button
                type="button"
                onClick={() => setDeleteConfirm(null)}
                className="flex-1 rounded-xl border border-gray-200 bg-white py-3 text-[14px] font-medium text-gray-600 active:bg-gray-50"
              >
                취소
              </button>
              <button
                type="button"
                onClick={handleDeleteConfirm}
                disabled={mutationLoading}
                className="flex-1 rounded-xl bg-red-500 py-3 text-[14px] font-semibold text-white active:bg-red-600 disabled:opacity-50"
              >
                {mutationLoading ? "삭제 중..." : "삭제"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
